<template>
  <BaseModal
    v-if="prompt"
    :name="CALENDAR_SYNC_PROMPT_MODAL_NAME"
    :click-outside-to-close="false"
    :escape-to-close="true"
    width-class="max-w-[500px]"
    modal-class="pt-32"
    :auto-focus="false"
    @show="handleShow"
    @hide="handleHide"
  >
    <div class="relative px-6 pt-8 pb-6 sm:px-10">
      <button
        type="button"
        class="absolute top-4 right-4 flex h-8 w-8 items-center justify-center rounded-full text-neutral-400 hover:bg-neutral-100 hover:text-neutral-700"
        aria-label="Close for today"
        title="Close for today"
        test_id="calendar-sync-prompt-close"
        @click="closeForToday"
      >
        <svg
          class="h-4 w-4"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          stroke-width="1.75"
          stroke-linecap="round"
          aria-hidden="true"
        >
          <path d="M3 3l10 10M13 3L3 13" />
        </svg>
      </button>

      <template v-if="step === 'ask'">
        <div class="flex justify-center" aria-hidden="true">
          <MentorIllustration v-if="prompt.role === 'mentor'" />
          <MemberIllustration v-else />
        </div>

        <div class="display-1 mt-6 text-center">{{ copy.headline }}</div>
        <p class="mt-3 text-center text-sm text-neutral-600">
          {{ copy.body }}
        </p>

        <div class="mt-6 flex justify-center">
          <BaseButton
            color="primary"
            size="large"
            test_id="calendar-sync-prompt-connect"
            @click="goToSettings"
          >
            {{ copy.button }}
          </BaseButton>
        </div>

        <div class="mt-4 text-center">
          <button
            type="button"
            class="text-xs text-neutral-500 underline-offset-2 hover:underline"
            test_id="calendar-sync-prompt-opt-out-link"
            @click="step = 'sign-off'"
          >
            I prefer to opt out
          </button>
        </div>
      </template>

      <template v-else>
        <div class="display-1 text-center">Before you opt out</div>
        <p class="mt-3 text-center text-sm text-neutral-600">
          Opting out turns this reminder off for good. From then on, keeping
          track of your sessions is fully on you.
        </p>

        <div class="mt-6 rounded-xl bg-neutral-50 p-4">
          <FormCheckbox v-model="agreed" test_id="calendar-sync-prompt-agree">
            <span class="text-sm text-neutral-700">{{ copy.agreement }}</span>
          </FormCheckbox>
        </div>

        <div
          class="mt-6 flex flex-col-reverse gap-3 sm:flex-row sm:justify-center"
        >
          <BaseButton
            color="outline-gray"
            size="large"
            test_id="calendar-sync-prompt-back"
            @click="step = 'ask'"
          >
            Back
          </BaseButton>
          <BaseButton
            color="outline-gray"
            size="large"
            :aria-disabled="agreed ? undefined : 'true'"
            :aria-describedby="agreed ? undefined : hintId"
            :class="{ 'opacity-50': !agreed }"
            test_id="calendar-sync-prompt-confirm"
            @click="confirmOptOut"
          >
            I take full responsibility
          </BaseButton>
        </div>
        <p
          v-if="!agreed"
          :id="hintId"
          class="mt-2 text-center text-xs text-neutral-500"
        >
          Tick the box to continue
        </p>

        <p class="mt-6 text-center text-xs text-neutral-500">
          Changed your mind later? Turn on sync anytime in Settings.
        </p>
      </template>
    </div>
  </BaseModal>
</template>

<script setup lang="ts">
import { computed, ref, useId } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import axios from 'axios'
import BaseModal from '@/shared/components/BaseModal.vue'
import BaseButton from '@/shared/components/BaseButton.vue'
import FormCheckbox from '@/shared/components/forms/FormCheckbox.vue'
import MemberIllustration from '@/shared/components/modals/calendar-sync-prompt/MemberIllustration.vue'
import MentorIllustration from '@/shared/components/modals/calendar-sync-prompt/MentorIllustration.vue'
import { useModals } from '@/shared/composables/useModals.js'
import {
  CALENDAR_SYNC_PROMPT_MODAL_NAME,
  CALENDAR_SYNC_PROMPT_SESSION_KEY,
} from '@/shared/data/globalVariables.js'

type Prompt = { role: 'member' | 'mentor'; reauth: boolean } | false

const CALENDAR_SETTINGS_PATH = '/settings/account/nylas_integration'
const SEEN_PATH = '/feature-interactions/calendar_sync_prompt/seen'
const OPT_OUT_PATH = '/feature-interactions/calendar_sync_prompt/opt_out'

const page = usePage()
const { closeModal } = useModals()

const prompt = computed<Prompt>(
  () =>
    (page.props.shared as { calendarSyncPrompt: Prompt }).calendarSyncPrompt,
)
const step = ref<'ask' | 'sign-off'>('ask')
const agreed = ref(false)
const hintId = `calendar-sync-prompt-hint-${useId()}`

// Copy locked with Jessica, 2026-08-13. The member view is a step more
// severe than our usual tone on purpose; the mentor view is benefit-led.
const COPY = {
  member: {
    headline: "Don't miss a session.",
    body: 'The most common reason people miss a session is a calendar that was never connected, and a missed session leaves the other person waiting. Connect yours and every booking is added automatically, with the time and the join link.',
    button: 'Connect your calendar',
    agreement:
      "I understand my sessions won't appear on my calendar. I'll track every session myself, and being there on time is on me.",
  },
  mentor: {
    headline: 'Streamline your scheduling.',
    body: 'Sync reads the busy time already on your calendar and closes those slots on GrowthMentor, so nobody can book you when you are not free. It works the other way too: every session you accept is added to your calendar.',
    button: 'Connect your calendar',
    agreement:
      "I understand sessions won't appear on my calendar and my busy time won't block bookings. I'll manage my schedule myself, and honoring every booked session is on me.",
  },
}

const REAUTH = {
  headline: 'Reconnect your calendar.',
  body: 'Your calendar connection stopped working, so sessions are not reaching your calendar right now. Reconnecting takes a minute and picks up where you left off.',
  button: 'Reconnect your calendar',
}

const copy = computed(() => {
  if (!prompt.value) return COPY.member
  const base = COPY[prompt.value.role]
  return prompt.value.reauth ? { ...base, ...REAUTH } : base
})

const rememberShownInThisTab = () => {
  try {
    sessionStorage.setItem(CALENDAR_SYNC_PROMPT_SESSION_KEY, '1')
  } catch {
    // Private mode or blocked storage. The server stamp still counts.
  }
}

const handleShow = () => {
  rememberShownInThisTab()
  axios.post(SEEN_PATH).catch(() => {})
}

const handleHide = () => {
  step.value = 'ask'
  agreed.value = false
}

const closeForToday = () => closeModal(CALENDAR_SYNC_PROMPT_MODAL_NAME)

const goToSettings = () => {
  closeModal(CALENDAR_SYNC_PROMPT_MODAL_NAME)
  router.visit(CALENDAR_SETTINGS_PATH)
}

const confirmOptOut = async () => {
  if (!agreed.value) return

  try {
    await axios.post(OPT_OUT_PATH)
  } finally {
    closeModal(CALENDAR_SYNC_PROMPT_MODAL_NAME)
  }
}
</script>
